import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useToast } from 'components/ui/use-toast';
import { useUndoDelete } from 'modules/trash';
import { QUERY_KEY as QUESTION_BANK_QUERY_KEY } from 'modules/questionBank/hooks';
import type { Subject } from './types';
import * as Api from './api';

export const QUERY_KEY = ['subjects'] as const;

export const useSubjectList = () => {
  return useQuery<Subject[]>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await Api.List();
      return res.data?.data ?? [];
    },
  });
};

export const useCreateSubject = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: Api.CreateSubjectData) => {
      const res = await Api.Create(data);
      return res.data?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success('Subject created');
    },
    onError: (error: any) => {
      toast.error('Error', {
        description: error?.response?.data?.message || 'Failed to create subject',
      });
    },
  });
};

export const useUpdateSubject = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Api.CreateSubjectData> }) => {
      const res = await Api.Update(id, data);
      return res.data?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success('Subject updated');
    },
    onError: (error: any) => {
      toast.error('Error', {
        description: error?.response?.data?.message || 'Failed to update subject',
      });
    },
  });
};

export const useDeleteSubject = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();
  const undoable = useUndoDelete();

  return useMutation({
    mutationFn: async (id: string) => {
      await Api.Delete(id);
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      // The subject's questions are deleted with it.
      queryClient.invalidateQueries({ queryKey: QUESTION_BANK_QUERY_KEY });
      undoable({ type: 'subject', ids: [id], title: t('trash.deleted_subject') });
    },
    onError: (error: any) => {
      toast.error('Error', {
        description: error?.response?.data?.message || 'Failed to delete subject',
      });
    },
  });
};
